/**
 * Raw Binom campaign detail (Main Options / Advanced fields). Used by the
 * Flow Optimizer drawer Campaign tab — not for the generic Campaign list
 * (that uses the unified shape).
 *
 * For Media Devoted | Created By Ciro Autiero
 */

import { useQuery } from '@tanstack/react-query';

import api from '@/services/api';
import { binomKeys } from './useBinom';

export interface BinomCampaignSettingsRaw {
  payoutPercent?: number;
  postbackPercent?: number;
  trafficLossPercent?: number;
  ea?: unknown;
  lpPixel?: unknown;
  s2sPostback?: unknown;
}

/**
 * Binom returns Cost + HideReferrer as nested objects. The drawer flattens
 * them into the meta-draft shape. Top-level mirror fields are kept for
 * forward compatibility / fixture data.
 */
export interface BinomCampaignCostRaw {
  model?: 'CPC' | 'CPM' | 'CPA' | string;
  money?: { amount?: number; currency?: string };
  isAuto?: boolean;
}

export interface BinomCampaignHideReferrerRaw {
  type?: string;
  domain?: string;
}

export interface BinomCampaignDetailRaw {
  id: number | string;
  name: string;
  groupUuid?: string;
  domainUuid?: string;
  trafficSourceId?: number;
  rotationId?: number | null;
  cost?: BinomCampaignCostRaw;
  hideReferrer?: BinomCampaignHideReferrerRaw;
  costModel?: 'CPC' | 'CPM' | 'CPA' | string;
  currency?: string;
  amount?: number;
  isAuto?: boolean;
  distributionType?: string;
  hideReferrerType?: string;
  hideReferrerDomain?: string;
  tags?: Array<string | number>;
  vpnProxyDetection?: boolean;
  botDetection?: boolean;
  notes?: string;
  key?: string;
  campaignSettings?: BinomCampaignSettingsRaw;
  [key: string]: unknown;
}

const rawKey = (campaignId: string) =>
  [...binomKeys.campaign(campaignId), 'raw'] as const;

export function useBinomCampaignRaw(campaignId: string | undefined) {
  return useQuery({
    queryKey: rawKey(campaignId ?? ''),
    queryFn: async () => {
      const r = await api.get<{ data: BinomCampaignDetailRaw }>(
        `/binom/campaigns/${encodeURIComponent(campaignId!)}/raw`,
      );
      return r.data?.data ?? null;
    },
    enabled: Boolean(campaignId),
    staleTime: 30_000,
  });
}
